import Table from '@shared/components/Table';
import useFetchData from '@shared/hooks/useFetchData';
import ActionButtons from '@shared/components/ActionButtons';
import MajorContractForm from './MajorContractForm';
import MajorContractTranslation from './MajorContractTranslation';
import DeleteModal from '@shared/components/DeleteModal';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { useState, useEffect } from 'react';
import { majorContractsService } from '@shared/services/majorContracts';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import useModal from "@shared/hooks/useModal";
import { useStatusBadge } from '@shared/components/StatusBadge';
import { useReportGenerator } from '@shared/hooks/useReportGenerator';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';


export default function MajorContracts() {

    const { t } = useTranslation();
    const { pageNumber, fetchData, pageSize, sortBy, sortDir, data, totalPages, totalElements, setpageNumber, setPageSize, searchQuery, setSearchQuery, handleSearchClick } = useFetchData();
    const [selectedActive, setSelectedActive] = useState('true');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const { statusBadge } = useStatusBadge();

    const formProps = useForm({
        defaultValues: {
            reasonKt: '',
            description: '',
            hrNssiReasonsId: '',
            termInMonts: '',
            termInYears: '',
            active: true
        }
    });


    useEffect(() => {
        const params = {
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
        };
        
        // Add active filter if selectedActive is set
        if (selectedActive !== null && selectedActive !== '') {
            params.active = selectedActive === 'true';
        }
        
        fetchData(majorContractsService.getMajorContracts, params);
    }, [pageNumber, pageSize, sortBy, sortDir, selectedActive]);

    const columns = [
        {
            key: 'code',
            label: t('staff.majorContracts.table.headers.code'),
        },
        {
            key: 'reasonKt',
            label: t('staff.majorContracts.table.headers.reasonKt'),
        },
        {
            key: 'description',
            label: t('staff.majorContracts.table.headers.description'),
        },
        {
            key: 'hrNssiReasonsName',
            label: t('staff.majorContracts.table.headers.hrNssiReasonsId'),
        },
        {
            key: 'termInMonts',
            label: t('staff.majorContracts.table.headers.termInMonts'),
        },
        {
            key: 'termInYears',
            label: t('staff.majorContracts.table.headers.termInYears'),
        },
        {
            key: 'active',
            label: t('staff.majorContracts.table.headers.active'),
        },
        {
            key: 'translated',
            label: t('staff.majorContracts.table.headers.translations'),
        },
    ]
    const searchOptions = [{ key: 'description', label: "description" }]

    // Report generator for major contracts
    const { 
        generatePdfReport, 
        generateExcelReport,
        pdfLoading,
        excelLoading
    } = useReportGenerator({
        title: t('staff.majorContracts.title'),
        columns: [
            { key: 'code', label: t('staff.majorContracts.table.headers.code') },
            { key: 'reasonKt', label: t('staff.majorContracts.table.headers.reasonKt') },
            { key: 'description', label: t('staff.majorContracts.table.headers.description') },
            { key: 'hrNssiReasonsName', label: t('staff.majorContracts.table.headers.hrNssiReasonsId') },
            { 
                label: 'Срок',
                children: [
                    { key: 'termInMonts', label: t('common.months') },
                    { key: 'termInYears', label: t('common.years') },
                    { key: 'termDescription', label: t('common.text') }
                ]
            }
        ],
        fileName: 'major_contracts_report',
        companyName: '',
        reportService: () => {
            const params = { searchQuery };
            if (selectedActive !== null && selectedActive !== '') {
                params.active = selectedActive === 'true';
            }
            return majorContractsService.getMajorContractsReportData(params);
        },
        customStyles: {}
    });

    const actionButtons = (
        <ActionButtons
            buttons={[{
                ...COMMON_BUTTONS.ADD,
                onClick: () => handleOpenModal('add')
            },
            {
                ...COMMON_BUTTONS.EDIT,
                onClick: () => handleOpenModal('edit')
            },
            {
                ...COMMON_BUTTONS.TRANSLATE,
                onClick: () => handleOpenModal('translate')
            },
            {
                ...COMMON_BUTTONS.DELETE,
                onClick: () => handleOpenModal('delete')
            },
            {
                ...COMMON_BUTTONS.DOWNLOAD_PDF,
                onClick: generatePdfReport,
                disabled: pdfLoading,
                icon: pdfLoading ? faSpinner : COMMON_BUTTONS.DOWNLOAD_PDF.icon,
                iconProps: pdfLoading ? { spin: true } : {},
                title: pdfLoading ? "Генериране на PDF..." : "common.downloadPDF",
                showWhen: 'always'
            },
            {
                ...COMMON_BUTTONS.EXCEL,
                onClick: generateExcelReport,
                disabled: excelLoading,
                icon: excelLoading ? faSpinner : COMMON_BUTTONS.EXCEL.icon,
                iconProps: excelLoading ? { spin: true } : {},
                title: excelLoading ? "Генериране на Excel..." : "common.downloadExcel",
                showWhen: 'always'
            }
            ]}
            selectedRecord={selectedRecord}
        />
    )

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };


    const handleFormSubmit = async (formData) => {
        try {
            if (modalState.type === 'edit' && selectedRecord) {
                await majorContractsService.updateMajorContract(selectedRecord.id, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await majorContractsService.createMajorContract(formData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            handleCloseModal();
            const params = {
                pageNumber,
                pageSize,
                sortBy,
                sortDir,
                searchQuery,
            };
            if (selectedActive !== null && selectedActive !== '') {
                params.active = selectedActive === 'true';
            }
            fetchData(majorContractsService.getMajorContracts, params);
        } catch (error) {
            console.error('Error saving record:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    };

    const handleConfirmDelete = useDeleteHandler({
        service: majorContractsService,
        entityName: 'MajorContract',
        selectedData: selectedRecord,
        fetchData,
        setSelectedData: setSelectedRecord,
        handleCloseModal,
        customFetchParams: {
            pageNumber,
            pageSize,
            sortBy,
            sortDir,
            searchQuery,
            ...(selectedActive !== null && selectedActive !== '' && { active: selectedActive === 'true' }),
        },
        successMessage: t('common.deletedSuccessfully'),
        errorMessage: t('common.errorMessage'),
    });

    return (
        <>
            <Table
                title={t('staff.majorContracts.title')}
                actionButtons={actionButtons}
                columns={columns}
                data={data}
                selectedData={selectedRecord?.id}
                handleSearchClick={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    handleSearchClick(majorContractsService.getMajorContracts, params);
                }}
                getData={() => {
                    const params = { searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        params.active = selectedActive === 'true';
                    }
                    fetchData(majorContractsService.getMajorContracts, params);
                }}
                handleCheckboxChange={(index) => handleSelectRecord(data[index])}
                pageNumber={pageNumber}
                showPageSizeSelector={false}
                showPagination={true}
                totalPages={totalPages}
                totalElements={totalElements}
                pageSize={pageSize}
                setpageNumber={setpageNumber}
                setPageSize={setPageSize}
                searchOptions={searchOptions}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showActiveSelect={true}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
                renderCell={(item, column) => statusBadge(item, column)}
            />

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.description || selectedRecord?.reasonKt}
                    entityType="record"
                />
            )}

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <MajorContractForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                    formProps={formProps}
                />
            )}

            {modalState.visible && modalState.type === 'translate' && (
                <MajorContractTranslation
                    onClose={handleCloseModal}
                    record={selectedRecord}
                    onRefresh={() => {
                        const params = {
                            pageNumber,
                            pageSize,
                            sortBy,
                            sortDir,
                            searchQuery,
                        };
                        if (selectedActive !== null && selectedActive !== '') {
                            params.active = selectedActive === 'true';
                        }
                        return fetchData(majorContractsService.getMajorContracts, params);
                    }}
                />
            )}
        </>
    )
}